import {useEffect, useRef, useState, useCallback} from 'react';

type DriverLocation = {
  latitude: number;
  longitude: number;
};

export default function useDriverTracking(driverId?: number) {
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeout = useRef<NodeJS.Timeout | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [location, setLocation] = useState<DriverLocation>({
    latitude: 0,
    longitude: 0,
  });
  const token = localStorage.getItem('token');
  const connect = useCallback(() => {
    if (!driverId) return;
    const socketUrl =
      process.env.REACT_APP_WEBSOCKET_URL ||
      'wss://ws-staging.magicdispatch.com/ws/track';
    const socket = new WebSocket(`${socketUrl}/${driverId}/?token=${token}`);
    ws.current = socket;
    socket.onopen = () => {
      console.log('✅ WebSocket connected');
      setIsConnected(true);
      setReconnectAttempts(0); // reset
    };

    socket.onmessage = (event) => {
      // console.log('📩 Incoming:', event.data);
      setLocation(JSON.parse(event.data));
    };

    socket.onerror = (error) => {
      // console.error('❌ WebSocket error:', error);
    };

    socket.onclose = (e) => {
      setIsConnected(false);
      // console.log('🔌 WebSocket closed:', e.code, e.reason);

      // // only reconnect if not manually closed
      const nextDelay = Math.min(10000, 2 ** reconnectAttempts * 1000);
      // console.log(`⏳ Reconnecting in ${nextDelay / 1000}s...`);

      reconnectTimeout.current = setTimeout(() => {
        setReconnectAttempts((prev) => prev + 1);
        connect();
      }, nextDelay);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driverId]);

  useEffect(() => {
    connect();

    return () => {
      if (reconnectTimeout.current) clearTimeout(reconnectTimeout.current);
      if (ws.current) ws.current.close();
    };
  }, [connect]);

  return {isConnected, location};
}
